import { FC, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { BreadcrumbProps, BreadcrumbsArray } from './breadcrumbs.types';
import { MoreButton, Separator } from './components';
import { BreadcrumbItem, BreadcrumbsWrapper } from './breadcrumbs.styled';
import { SVG } from 'components';
import { Icons } from 'assets';

/**
 * Компонент для отображения навигационной цепочки (breadcrumbs).
 * Отображает первый, предпоследний (если есть) и последний элементы.
 * Если есть скрытые элементы, отображает кнопку с троеточием для их раскрытия.
 *
 * @param {BreadcrumbProps} props - Пропсы компонента.
 * @param {BreadcrumbsArray} props.crumbs - Массив элементов навигационной цепочки (минимум 2 элемента).
 * @returns {JSX.Element | null} - Возвращает JSX-элемент или null, если массив crumbs пуст или имеет менее 2 элементов.
 * @example
 * ```tsx
 * const crumbs: BreadcrumbsArray = [
 *   { label: 'Главная', href: '/' },
 *   { label: 'Каталог', href: '/catalog' },
 *   { label: 'Продукт', href: '/catalog/product' },
 * ];
 * <Breadcrumbs crumbs={crumbs} />
 * ```
 */
export const Breadcrumbs: FC<BreadcrumbProps> = observer(({ crumbs }) => {
  const firstCrumb = useMemo(() => crumbs[0], [crumbs]);
  const lastCrumb = useMemo(() => crumbs[crumbs.length - 1], [crumbs]);
  const secondLastCrumb = useMemo(
    () => (crumbs.length > 2 ? crumbs[crumbs.length - 2] : null),
    [crumbs]
  );

  const hiddenCrumbs = useMemo(
    () => crumbs.slice(1, crumbs.length - 2),
    [crumbs]
  );

  return crumbs.length > 1 ? (
    <BreadcrumbsWrapper aria-label="breadcrumb">
      {/* Первый элемент всегда виден */}
      <BreadcrumbItem
        to={firstCrumb.href}
        $isLast={false}
        title={firstCrumb.label}
      >
        {firstCrumb.label === '.' ? <SVG src={Icons.Home} /> : firstCrumb.label}
      </BreadcrumbItem>

      {/* Если есть скрытые элементы, отображаем кнопку троеточие */}
      {!!hiddenCrumbs.length && (
        <>
          <Separator />
          <MoreButton crumbs={hiddenCrumbs} />
        </>
      )}

      {/* Предпоследний элемент всегда виден, если он не совпадает с первым или последним */}
      {!!secondLastCrumb && secondLastCrumb !== firstCrumb && (
        <>
          <Separator />
          <BreadcrumbItem
            to={secondLastCrumb.href}
            $isLast={false}
            title={secondLastCrumb.label}
          >
            {secondLastCrumb.label}
          </BreadcrumbItem>
        </>
      )}

      <Separator />

      {/* Последний элемент всегда виден */}
      <BreadcrumbItem
        to={lastCrumb.href}
        title={lastCrumb.label}
        aria-current={'page'}
        $isLast
      >
        {lastCrumb.label}
      </BreadcrumbItem>
    </BreadcrumbsWrapper>
  ) : null;
});
